import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import type { NotificationPreferencesDto } from "shared";
import { DEFAULT_NOTIFICATION_PREFERENCES } from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { QueryKeys } from "../LinkGen";

export const TabNotifications = () => {
	const company = useCompany();
	const emit = useAlertEmit();
	const queryClient = useQueryClient();
	const [prefs, setPrefs] = useState<NotificationPreferencesDto>(
		DEFAULT_NOTIFICATION_PREFERENCES,
	);

	const query = useQuery({
		queryKey: QueryKeys.Notifications.Preferences(company.id),
		queryFn: async () => (await Api.GetNotificationPreferences()).data,
	});

	useEffect(() => {
		if (query.data) {
			setPrefs(query.data);
		}
	}, [query.data]);

	const save = useMutation({
		mutationFn: async (next: NotificationPreferencesDto) => {
			const { data } = await Api.UpdateNotificationPreferences(next);
			return data;
		},
		onSuccess: async (data) => {
			setPrefs(data);
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Notifications.Preferences(company.id),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Notification preferences saved",
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Could not save notification preferences",
			});
		},
	});

	const toggle = (key: keyof NotificationPreferencesDto) => {
		setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	if (query.isLoading) {
		return (
			<div className="d-flex justify-content-center py-5">
				<Spinner />
			</div>
		);
	}

	return (
		<>
			<h2 className="mb-2">Notifications</h2>
			<p className="text-muted mb-4">
				Choose which alerts this company receives. Changes apply to in-app
				notifications and emails for all team members.
			</p>
			<Row>
				<Col md={6}>
					<Form
						onSubmit={(e) => {
							e.preventDefault();
							void save.mutateAsync(prefs);
						}}
					>
						<h3 className="h6 mb-3">Post publishing</h3>

						<Form.Group className="mb-3">
							<Form.Check
								type="switch"
								id="notif-failed-email"
								label="Email when a post fails to publish"
								checked={prefs.postPublishFailedEmail}
								onChange={() => toggle("postPublishFailedEmail")}
							/>
						</Form.Group>

						<Form.Group className="mb-3">
							<Form.Check
								type="switch"
								id="notif-failed-inapp"
								label="In-app notification when a post fails to publish"
								checked={prefs.postPublishFailedInApp}
								onChange={() => toggle("postPublishFailedInApp")}
							/>
						</Form.Group>

						<Form.Group className="mb-4">
							<Form.Check
								type="switch"
								id="notif-success-inapp"
								label="In-app notification when a post publishes successfully"
								checked={prefs.postPublishSuccessInApp}
								onChange={() => toggle("postPublishSuccessInApp")}
							/>
						</Form.Group>
						<Button type="submit" loading={save.isPending}>
							Save
						</Button>
					</Form>
				</Col>
			</Row>
		</>
	);
};
